/**
 * Shared building blocks for the AGW / ACN detail pages (design §10.4):
 * read-only fact fields, the device-reported spec card, the certificate card,
 * the per-component health card, and the generate-token section for a node
 * awaiting enrolment.
 */

import { useState } from 'react';
import { Activity, Cpu, KeyRound, ShieldCheck } from 'lucide-react';
import Modal from '../common/Modal';
import ClaimTokenPanel from './ClaimTokenPanel';
import { certExpiringSoon, fmtDate } from './nodeStatus';
import { useToast } from '../common/ToastContainer';
import accessService, {
  type AccessHealthState,
  type MintedClaim,
  type NodeComponentHealth,
  type NodeDeviceSpec,
} from '../../services/access.service';

export const Field = ({ label, value, mono }: { label: string; value: string | number | null | undefined; mono?: boolean }) => (
  <div>
    <div className="text-xs text-gray-500">{label}</div>
    <div className={`break-all text-gray-900 ${mono ? 'font-mono text-xs' : 'text-sm'}`}>
      {value === null || value === undefined || value === '' ? '—' : value}
    </div>
  </div>
);

const COMPONENT_STATE_STYLE: Record<AccessHealthState, string> = {
  up: 'bg-success-100 text-success-700 border-success-200',
  degraded: 'bg-warning-100 text-warning-700 border-warning-200',
  down: 'bg-danger-100 text-danger-700 border-danger-200',
  unknown: 'bg-gray-100 text-gray-600 border-gray-200',
};

/**
 * Per-component health from the node's last heartbeat — the list its single
 * `health` value is rolled up from (worst wins), so this is where an operator
 * sees WHICH part is down. `stale` is set when the node's effective health has
 * aged to unknown: the rows are then the last thing it said, not its state now.
 */
export const ComponentHealthCard = ({
  components,
  reportedAt,
  stale,
}: {
  components: NodeComponentHealth[] | null | undefined;
  reportedAt: string | null;
  stale?: boolean;
}) => (
  <div className="card" data-testid="node-components">
    <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-900">
      <Activity className="h-4 w-4 text-teal-600" /> Components
      <span className="text-xs font-normal text-gray-400">
        from the last heartbeat{reportedAt ? ` · ${fmtDate(reportedAt)}` : ''}
      </span>
    </h2>
    {stale && components && components.length > 0 && (
      <p className="mb-2 text-xs text-warning-700">
        The node has not reported recently — these are its last reported states, not its current ones.
      </p>
    )}
    {!components || components.length === 0 ? (
      <p className="text-sm text-gray-500">
        {components ? 'The last heartbeat reported no components.' : 'No component health has been reported yet.'}
      </p>
    ) : (
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 text-left text-xs text-gray-500">
            <th className="py-1.5 pr-4 font-medium">Component</th>
            <th className="py-1.5 pr-4 font-medium">State</th>
            <th className="py-1.5 font-medium">Detail</th>
          </tr>
        </thead>
        <tbody>
          {components.map((c) => (
            <tr key={c.name} className="border-b border-gray-100 last:border-0" data-testid={`node-component-${c.name}`}>
              <td className="py-1.5 pr-4 font-mono text-xs text-gray-900">{c.name}</td>
              <td className="py-1.5 pr-4">
                <span
                  className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${
                    COMPONENT_STATE_STYLE[c.state] ?? COMPONENT_STATE_STYLE.unknown
                  }`}
                >
                  {c.state}
                </span>
              </td>
              <td className="break-all py-1.5 text-xs text-gray-600">{c.detail || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);

/** Node-reported hardware/OS facts — read-only by design (§10.3). */
export const DeviceSpecCard = ({ spec }: { spec: NodeDeviceSpec | null }) => (
  <div className="card">
    <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-900">
      <Cpu className="h-4 w-4 text-teal-600" /> Device spec
      <span className="text-xs font-normal text-gray-400">reported by the node at enrolment</span>
    </h2>
    {!spec || Object.keys(spec).length === 0 ? (
      <p className="text-sm text-gray-500">
        Nothing reported yet — the node pushes its hardware and OS facts when it enrols.
      </p>
    ) : (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-x-6 gap-y-2 md:grid-cols-4">
          <Field label="Hostname" value={spec.hostname} mono />
          <Field label="OS" value={spec.os && spec.osVersion ? `${spec.os} ${spec.osVersion}` : spec.os} />
          <Field label="Kernel" value={spec.kernel} mono />
          <Field label="Architecture" value={spec.arch} />
          <Field label="CPU cores" value={spec.cpuCores} />
          <Field label="Memory" value={spec.memoryMb ? `${Math.round(spec.memoryMb / 1024)} GiB` : undefined} />
          <Field label="Disk" value={spec.diskGb ? `${spec.diskGb} GB` : undefined} />
          <Field label="Virtualization" value={spec.virtualization} />
        </div>
        {spec.nics && spec.nics.length > 0 && (
          <div>
            <div className="mb-1 text-xs text-gray-500">Network interfaces</div>
            <div className="flex flex-wrap gap-2">
              {spec.nics.map((nic) => (
                <span key={nic.name} className="rounded border border-gray-200 bg-gray-50 px-2 py-1 text-xs text-gray-700">
                  <span className="font-medium">{nic.name}</span>
                  {nic.ipv4 && nic.ipv4.length > 0 && <span className="ml-1 font-mono">{nic.ipv4.join(', ')}</span>}
                  {nic.mac && <span className="ml-1 font-mono text-gray-400">{nic.mac}</span>}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    )}
  </div>
);

export const CertificateCard = ({
  serial,
  issuedAt,
  expiresAt,
}: {
  serial: string | null;
  issuedAt: string | null;
  expiresAt: string | null;
}) => {
  const expiringSoon = certExpiringSoon(expiresAt);
  return (
    <div className="card">
      <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-900">
        <ShieldCheck className="h-4 w-4 text-teal-600" /> Device certificate
      </h2>
      {serial ? (
        <div className="grid grid-cols-2 gap-x-6 gap-y-2 md:grid-cols-3">
          <Field label="Serial" value={serial} mono />
          <Field label="Issued" value={fmtDate(issuedAt)} />
          <div>
            <div className="text-xs text-gray-500">Expires</div>
            <div className={`text-sm ${expiringSoon ? 'font-medium text-warning-700' : 'text-gray-900'}`}>
              {fmtDate(expiresAt)}
              {expiringSoon && ' — soon'}
            </div>
          </div>
        </div>
      ) : (
        <p className="text-sm text-gray-500">
          Not enrolled — no certificate has been issued. Generate a registration token below and run the
          bootstrap on the node.
        </p>
      )}
    </div>
  );
};

/**
 * Mint (or re-mint) a registration token for a pre-registered node. Shown for
 * nodes awaiting enrolment; the token is displayed once in a modal.
 */
export const MintTokenSection = ({
  kind,
  nodeRowId,
  nodeName,
  nodeId,
  onMinted,
}: {
  kind: 'gateway' | 'app_connector';
  nodeRowId: string;
  nodeName: string;
  /** The registered node_id — the CN the bootstrap must enrol with. */
  nodeId: string;
  onMinted?: () => void;
}) => {
  const toast = useToast();
  const [minting, setMinting] = useState(false);
  const [claim, setClaim] = useState<MintedClaim | null>(null);

  const mint = async () => {
    try {
      setMinting(true);
      const minted = await accessService.mintNodeClaim(kind, nodeRowId, 7);
      setClaim(minted);
      onMinted?.();
    } catch (err) {
      toast.error('Token minting failed', err instanceof Error ? err.message : 'Could not mint a token');
    } finally {
      setMinting(false);
    }
  };

  return (
    <>
      <button type="button" className="btn-primary inline-flex items-center gap-2" onClick={() => void mint()} disabled={minting}>
        <KeyRound className="h-4 w-4" />
        {minting ? 'Minting…' : 'Generate registration token'}
      </button>
      <Modal isOpen={!!claim} onClose={() => setClaim(null)} title={`${nodeName} — install & enrol`} size="lg"
        footer={
          <div className="flex justify-end">
            <button type="button" className="btn-primary" onClick={() => setClaim(null)}>
              I've stored the token
            </button>
          </div>
        }
      >
        {claim && <ClaimTokenPanel claim={claim} kind={kind} nodeName={nodeName} nodeId={nodeId} />}
      </Modal>
    </>
  );
};
